/**
 * MAHI discovery feed — starts and polls one try-on job for one product.
 *
 * Lives per-card (see ProductSwipeCard) rather than behind a full-screen
 * modal: the result replaces that card's own photo in place once it lands,
 * so there is nothing to hand back up to the feed except an analytics event.
 *
 * This is also the wall - the single point in the product where browsing stops
 * being enough and MAHI needs to know who she is dressing. A guest gets a 409
 * from POST /tryon/jobs (require_registered_user) and is sent to signup rather
 * than shown an error, because "seeing this on yourself" is exactly the ask
 * that makes signing up worth it. Everything they have swiped comes with them:
 * signup claims the account they have been using rather than creating a new
 * one (see pages/Signup.jsx).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mahi, { errMsg } from '../../api';

const POLL_INTERVAL_MS = 2500;
const RUNNING_STATUSES = new Set(['queued', 'tryon_running', 'tryon_ready', 'size_running']);
// A momentary network blip during a ~20s poll loop is normal, not a failed
// job — one dropped request used to end the job outright with nothing wrong
// server-side. Only give up once several checks in a row can't reach the
// backend at all.
const POLL_FAILURE_TOLERANCE = 3;

export default function useTryOnJob(miraSku) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle'); // idle | running | ready | failed
  const [job, setJob] = useState(null);
  // Why it failed, in a form worth showing someone — a job the backend
  // explicitly rejected (no photo on file, daily limit) carries a real
  // reason (job.error, or the request's own error detail) that used to be
  // thrown away in favor of one generic toast regardless of cause.
  const [error, setError] = useState('');
  const pollRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => () => {
    cancelledRef.current = true;
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const poll = useCallback((jobId) => {
    let consecutiveFailures = 0;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await mahi.get(`/tryon/jobs/${jobId}`);
        if (cancelledRef.current) return;
        consecutiveFailures = 0;
        setJob(data);
        if (data.status === 'tryon_failed') {
          clearInterval(pollRef.current);
          setError(data.error || '');
          setStatus('failed');
        } else if (!RUNNING_STATUSES.has(data.status) || data.image_url) {
          // Either a terminal state, or tryon_ready/size_running with the
          // image already in — either way there's nothing left worth
          // polling for from this card's point of view.
          clearInterval(pollRef.current);
          setStatus('ready');
        }
      } catch {
        if (cancelledRef.current) return;
        consecutiveFailures += 1;
        if (consecutiveFailures >= POLL_FAILURE_TOLERANCE) {
          clearInterval(pollRef.current);
          setError("Lost track of that try-on - your connection may have dropped.");
          setStatus('failed');
        }
      }
    }, POLL_INTERVAL_MS);
  }, []);

  const start = useCallback(async () => {
    setStatus('running');
    setJob(null);
    setError('');
    try {
      const { data } = await mahi.post('/tryon/jobs', { mira_sku: miraSku });
      if (cancelledRef.current) return;
      setJob(data);
      if (data.status === 'tryon_failed') {
        setError(data.error || '');
        setStatus('failed');
      } else if (data.image_url) {
        setStatus('ready');
      } else if (RUNNING_STATUSES.has(data.status)) {
        poll(data.job_id);
      } else {
        setStatus('failed');
      }
    } catch (err) {
      if (cancelledRef.current) return;
      // 409 is the guest gate, not a failure: they asked for the one thing
      // that needs an account. Send them to claim it, and remember what they
      // were trying on so the tap can be honoured on the way back rather than
      // having to be found again.
      if (err?.response?.status === 409) {
        setStatus('idle');
        navigate('/signup', { state: { from: '/discover', tryOnSku: miraSku } });
        return;
      }
      // A request the backend explicitly rejected (no photo on file, daily
      // limit, item no longer available) has a real reason worth showing —
      // not the same as "something broke, try again."
      setError(errMsg(err, ''));
      setStatus('failed');
    }
  }, [miraSku, poll, navigate]);

  return { start, status, job, error };
}
